import React from 'react';

import {
  Alert,
  Badge,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Spinner,
} from '../../../components/ui';
import { BVStudioCompetitorContext } from '../types';

function formatRating(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return 'N/A';
  }
  return `${value.toFixed(1)}/5`;
}

function formatPercent(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return 'N/A';
  }
  return `${Math.round(value)}%`;
}

interface CompetitorPanelProps {
  isLoading: boolean;
  error: string | null;
  context: BVStudioCompetitorContext | null;
}

function BrandMetricsCard({
  title,
  brand,
  reviewCount,
  productCount,
  avgRating,
  recommendPct,
  summary,
}: {
  title?: string;
  brand: string;
  reviewCount: number;
  productCount: number;
  avgRating: number | null;
  recommendPct: number | null;
  summary?: string;
}) {
  return (
    <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          {title ? (
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
              {title}
            </p>
          ) : null}
          <h3 className="text-base font-semibold text-charcoal dark:text-gray-100">{brand}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {reviewCount} reviews across {productCount} products
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="default">Avg rating: {formatRating(avgRating)}</Badge>
          <Badge variant="default">Recommend: {formatPercent(recommendPct)}</Badge>
        </div>
      </div>

      {summary ? (
        <div className="space-y-2 rounded-xl bg-gray-50 p-3 dark:bg-dark-surface-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
            Competitive position
          </p>
          <p className="text-sm leading-6 text-charcoal dark:text-gray-100">{summary}</p>
        </div>
      ) : null}
    </section>
  );
}

export default function CompetitorPanel({
  isLoading,
  error,
  context,
}: CompetitorPanelProps) {
  return (
    <Panel className="flex h-full min-h-0 flex-col overflow-hidden">
      <PanelHeader className="shrink-0">
        <div>
          <PanelTitle>Competitor context</PanelTitle>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Read-only context for the current setup.
          </p>
        </div>
      </PanelHeader>

      <PanelBody className="min-h-0 flex-1 space-y-5 overflow-y-auto">
        {isLoading ? (
          <div className="flex min-h-[280px] items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="error" title="Could not load competitor context">
            {error}
          </Alert>
        ) : context ? (
          <>
            <BrandMetricsCard
              title="Selected brand"
              brand={context.primary_brand}
              reviewCount={context.primary_metrics.review_count}
              productCount={context.primary_metrics.product_count}
              avgRating={context.primary_metrics.avg_rating}
              recommendPct={context.primary_metrics.recommend_pct}
              summary={context.position_summary}
            />

            {context.competitors.length === 0 ? (
              <Alert variant="info" title="No competitor reviews found">
                No competitor reviews matched the current setup.
              </Alert>
            ) : (
              <div className="space-y-4">
                {context.competitors.map((competitor) => (
                  <section
                    key={competitor.brand}
                    className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-charcoal dark:text-gray-100">
                          {competitor.brand}
                        </h3>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {competitor.review_count} reviews across {competitor.product_count} products
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="default">Avg rating: {formatRating(competitor.avg_rating)}</Badge>
                        <Badge variant="default">Recommend: {formatPercent(competitor.recommend_pct)}</Badge>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                        Summary
                      </p>
                      <p className="text-sm leading-6 text-charcoal dark:text-gray-100">{competitor.summary}</p>
                    </div>

                    <div className="space-y-2 rounded-xl bg-gray-50 p-3 dark:bg-dark-surface-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                        How it differs
                      </p>
                      <p className="text-sm leading-6 text-charcoal dark:text-gray-100">
                        {competitor.differentiators}
                      </p>
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        ) : (
          <Alert variant="info" title="Compare is ready">
            Generate a draft and open Compare to load competitor context.
          </Alert>
        )}
      </PanelBody>
    </Panel>
  );
}
